import React, { useEffect } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    // Enable page scrolling on this route while restoring room overflow on unmount
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "hidden";
    };
  }, []);

  return (
    <div
      className="grain"
      style={{
        minHeight: "100vh",
        height: "100vh",
        maxHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        background: "var(--paper)",
        color: "var(--ink)",
        padding: "clamp(24px, 5vh, 64px) clamp(20px, 6vw, 96px)",
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto", paddingBottom: 48 }}>
        {/* Back Link & Brand */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
          <Link
            to="/"
            className="font-mono"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--ink)",
              textDecoration: "none",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 700,
              padding: "6px 14px",
              border: "1.5px solid var(--ink)",
              borderRadius: 999,
            }}
          >
            ← Back to Room
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 8, height: 8, background: "var(--neon-magenta)", display: "inline-block" }} />
            <span className="font-mono" style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Dard-e-Disco // Architecture &amp; Privacy
            </span>
          </div>
        </div>

        {/* Title */}
        <h1
          className="font-serif"
          style={{
            fontSize: "clamp(36px, 5vw, 68px)",
            lineHeight: 1.05,
            margin: "0 0 16px",
            letterSpacing: "-0.02em",
          }}
        >
          Privacy Policy &amp; <br />
          <em style={{ color: "var(--neon-magenta)" }}>Zero-Egress</em> Architecture
        </h1>

        <div className="font-mono text-muted" style={{ fontSize: 12, letterSpacing: "0.14em", marginBottom: 32 }}>
          EFFECTIVE DATE: SEPTEMBER 2026 · VERSION 1.0 · 100% CLIENT-SIDE WEB EXPERIMENT
        </div>

        {/* Highlight Callout: Optical Mirror Principle */}
        <div
          style={{
            background: "var(--ink)",
            color: "var(--paper)",
            padding: "24px 28px",
            borderRadius: 12,
            marginBottom: 40,
            borderLeft: "6px solid var(--neon-cyan)",
          }}
        >
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: "0.18em", color: "var(--neon-cyan)", marginBottom: 8 }}>
            CORE ARCHITECTURE // THE MIRROR PRINCIPLE
          </div>
          <p className="font-mono" style={{ fontSize: 13, lineHeight: 1.7, margin: 0, opacity: 0.9 }}>
            Dard-e-Disco functions like <strong>a mirror on your wall</strong>. It reflects your real-time head and body movements into a procedural 3D room. Like a mirror, it does not record footage or store your data, and your video stream stays entirely on your own device.
          </p>
        </div>

        {/* Policy Body */}
        <div className="font-mono" style={{ fontSize: 13, lineHeight: 1.8, color: "var(--ink)" }}>
          <section style={{ marginBottom: 36 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              1. 100% Local Inference &amp; No Backend
            </h2>
            <p style={{ margin: "0 0 12px", color: "var(--ink-soft)" }}>
              Dard-e-Disco runs completely inside your local web browser. All computer vision models (MediaPipe FaceMesh and Pose) are bundled as local static assets and executed via WebAssembly (WASM) and TensorFlow Lite on your device&apos;s CPU and GPU.
            </p>
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>
              There is zero backend server for processing video, zero API gateway, and zero remote cloud inference.
            </p>
          </section>

          <section style={{ marginBottom: 36 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              2. Camera Sensor Access &amp; In-Memory Lifecycle
            </h2>
            <p style={{ margin: "0 0 12px", color: "var(--ink-soft)" }}>
              When you grant camera permission, your browser&apos;s native <code>getUserMedia</code> API opens a local video stream directly to an in-memory <code>&lt;video&gt;</code> element.
            </p>
            <ul style={{ margin: "0 0 12px", paddingLeft: 20, color: "var(--ink-soft)" }}>
              <li>
                <strong>Purpose:</strong> Frames are passed directly into the WebAssembly runtime to calculate relative head yaw/pitch angles and 33 body joint positions.
              </li>
              <li>
                <strong>Immediate Discard:</strong> Each video frame is overwritten by the next frame on the subsequent <code>requestAnimationFrame</code> tick. No video frames are serialized, converted to base64, cached to disk, or recorded.
              </li>
              <li>
                <strong>Immediate Session Termination:</strong> Closing the tab, clicking &ldquo;Exit&rdquo;, or navigating away immediately stops all camera tracks, releasing your webcam hardware.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: 36 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              3. Browser-Enforced Content Security Policy (CSP)
            </h2>
            <p style={{ margin: "0 0 12px", color: "var(--ink-soft)" }}>
              Instead of asking you to rely on trust alone, this application relies on browser security enforcement. Our strict <code>Content-Security-Policy</code> restricts outbound connections:
            </p>
            <pre
              style={{
                background: "rgba(10,10,15,0.06)",
                border: "1px solid rgba(10,10,15,0.15)",
                padding: "12px 16px",
                borderRadius: 8,
                fontSize: 12,
                overflowX: "auto",
              }}
            >
              connect-src &apos;self&apos; ws: wss: blob: data:;
            </pre>
            <p style={{ margin: "8px 0 0", color: "var(--ink-soft)" }}>
              Your browser&apos;s internal networking engine will actively block and fail any attempted outbound network request to any third-party domain or external server.
            </p>
          </section>

          <section style={{ marginBottom: 36 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              4. Zero Cookies, Zero Telemetry &amp; Zero Trackers
            </h2>
            <p style={{ margin: "0 0 12px", color: "var(--ink-soft)" }}>
              Dard-e-Disco operates with zero commercial trackers:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, color: "var(--ink-soft)" }}>
              <li>No tracking cookies or marketing pixels (no Google Analytics, no Meta Pixel).</li>
              <li>No session recording tools (no Hotjar, LogRocket, or FullStory).</li>
              <li>No user accounts, email collection, or behavioral profiling.</li>
              <li>Microphone, geolocation, and payment APIs are explicitly blocked via the <code>Permissions-Policy</code> header.</li>
            </ul>
          </section>

          <section style={{ marginBottom: 36 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              5. Independent Third-Party Audits &amp; Verification
            </h2>
            <p style={{ margin: "0 0 12px", color: "var(--ink-soft)" }}>
              You can independently verify our claims using any recognized public web scanner:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, color: "var(--ink-soft)" }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Blacklight by The Markup:</strong> Verifies the absence of ad trackers, cookies, canvas fingerprinting, and session recording scripts. (<a href="https://themarkup.org/blacklight" target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink)", fontWeight: 700 }}>themarkup.org/blacklight</a>)
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Mozilla Observatory:</strong> Validates Content Security Policy headers and browser-enforced isolation. (<a href="https://observatory.mozilla.org" target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink)", fontWeight: 700 }}>observatory.mozilla.org</a>)
              </li>
              <li>
                <strong>URLScan.io:</strong> Real-time network sandbox recording all outgoing connections and requests. (<a href="https://urlscan.io" target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink)", fontWeight: 700 }}>urlscan.io</a>)
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: 48 }}>
            <h2 className="font-serif" style={{ fontSize: 26, margin: "0 0 12px", color: "var(--ink)" }}>
              6. Client-Side Source Integrity &amp; Verification
            </h2>
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>
              All client-side code, scripts, WebAssembly assets, and spatial audio routines execute transparently in your browser session. You can inspect all running scripts, network calls, and memory directly through your browser&apos;s Developer Tools (F12) to verify that zero data is ever collected or transmitted.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid rgba(10,10,15,0.15)",
            paddingTop: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <span className="font-mono text-muted" style={{ fontSize: 11, letterSpacing: "0.14em" }}>
            DARD-E-DISCO // 100% PRIVATE CLIENT-SIDE 3D EXPERIMENT
          </span>
          <Link
            to="/"
            className="font-mono"
            style={{
              color: "var(--ink)",
              fontSize: 12,
              fontWeight: 700,
              textDecoration: "underline",
              letterSpacing: "0.1em",
            }}
          >
            Return to Room →
          </Link>
        </div>
      </div>
    </div>
  );
}
